import apiClient from './api';

// Define interface for collection
export interface CollectionInfo {
  id: string;
  name: string;
  description: string;
}

// Define interface for field
export interface FieldInfo {
  id: string;
  name: string;
  type: string;
  isRequired: boolean;
}

// API response interfaces
interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
}

// Debug response interfaces
interface DebugCollectionsResponse {
  success: boolean;
  collections1: string[];
  collections2: string[];
  connectionInfo: {
    models: string[];
    [key: string]: any;
  };
  message: string;
}

interface DebugFieldsResponse {
  success: boolean;
  collection: string;
  fields: FieldInfo[];
  message: string;
}

// For debugging purposes
const logEndpointAttempt = (endpoint: string) => {
  console.log(`Attempting to call API endpoint: ${endpoint}`);
  const token = localStorage.getItem('accessToken');
  console.log(`Auth token available: ${!!token}`);
};

const metadataService = {
  /**
   * دریافت لیست کالکشن‌های موجود
   *
   * ✅ از /admin/metadata/collections استفاده می‌کنیم که در production هم با احراز هویت
   * ادمین فعال است. مسیر قدیمی /debug/collections فقط در حالت development کار می‌کرد
   * و در سرور 404 می‌داد (برای همین لیست خالی نشان داده می‌شد).
   */
  getCollections: async (): Promise<CollectionInfo[]> => {
    const endpoint = '/admin/metadata/collections';
    logEndpointAttempt(endpoint);

    try {
      // تایپ apiClient ادعا می‌کند پاسخ unwrap شده، ولی واقعاً AxiosResponse
      // برمی‌گرداند. هر دو حالت را به‌صورت دفاعی هندل می‌کنیم تا داده‌ی
      // {status, data:[...]} از اعماق response استخراج شود.
      const raw: any = await apiClient.get(endpoint);
      const body = raw?.data !== undefined ? raw.data : raw;
      const list = body?.data ?? body;

      if (Array.isArray(list)) return list as CollectionInfo[];

      // Fallback نسخه‌ی debug قدیمی
      const debug = body as DebugCollectionsResponse;
      if (debug && debug.success && debug.connectionInfo?.models) {
        return debug.connectionInfo.models.map((modelName) => ({
          id: modelName,
          name: modelName,
          description: `مدل ${modelName}`,
        }));
      }

      console.error('Unexpected metadata response format:', body);
      return [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  },

  /**
   * دریافت فیلدهای یک کالکشن
   * (همان دلیل بالا — مسیر admin به‌جای debug)
   */
  getCollectionFields: async (collectionId: string): Promise<FieldInfo[]> => {
    const endpoint = `/admin/metadata/collections/${collectionId}/fields`;
    logEndpointAttempt(endpoint);

    try {
      const raw: any = await apiClient.get(endpoint);
      const body = raw?.data !== undefined ? raw.data : raw;
      const list = body?.data ?? body;

      if (Array.isArray(list)) return list as FieldInfo[];

      const debug = body as DebugFieldsResponse;
      if (debug && debug.success && Array.isArray(debug.fields)) {
        return debug.fields;
      }

      console.error(`Unexpected metadata fields response format for ${collectionId}:`, body);
      return [];
    } catch (error) {
      console.error(`Error fetching fields for collection ${collectionId}:`, error);
      throw error;
    }
  },
};

export default metadataService; 